import {
  ModuleBanner,
  ModuleCampaignBanner,
  ModuleDamageExplainer,
  ModuleDisclaimer,
  ModuleFeatureIntro,
  ModuleLandingPageVoucherEntry,
  ModuleSection,
  ModuleStickyVinSearch,
  PageModular,
  PageModularModuleWrapper,
} from "@carfax/contentful/PageModularModule";
import { ModuleContentType } from "@carfax/contentful/types/content-type";
import Seo from "domain/SeoAndAnalytics/Seo";
import React, { useEffect, useMemo } from "react";
import { ModularPageComponent, ModulesWithTheme } from "@carfax/web-ui/components/ModulesWithTheme";
import ModularPageComponentWrapper from "./ModularPageComponentWrapper";
import { PricingPackage } from "domain/Pricing/PricingPage/Pricing.types";
import { ThemeName } from "@carfax/web-ui/components/ModulesWithTheme/utils/useTheme";
import { isEven } from "utils/integer";
import { useLocale, useTranslations } from "next-intl";
import { languageConfig } from "i18n/language-config";
import { modularPageSchemaHelpers } from "domain/SeoAndAnalytics/utils/modularPageSchemaHelpers";
import { getFullCanonicalUrl } from "domain/SeoAndAnalytics/utils/getFullCanonicalUrl";
import {
  buildModularPageBreadcrumbs,
  toAbsoluteBreadcrumbs,
  CARFAX_BASE_URL,
} from "./utils/buildModularPageBreadcrumbs";
import { useBreadcrumbs } from "contexts";
import { Routes } from "utils/config/routes";
import RouterService from "../Routing/utils/router-service";
import { useLanguage } from "hooks/useLanguage";
import {
  buildHrefLangAlternatesForModularPage,
  normalizeCmsHrefLangEntries,
} from "domain/SeoAndAnalytics/hrefLangAlternates";
import { useModuleLoaders } from "domain/CommonModules/moduleLoaders/ModuleLoadersContext";

const fixedModules = [
  ModuleContentType.ModuleBanner,
  ModuleContentType.ModuleDisclaimer,
  ModuleContentType.ModuleCampaignBanner,
  ModuleContentType.ModuleLandingPageVoucherEntry,
  ModuleContentType.ModuleDamageExplainer,
];

export interface ModularPageProps {
  modularPage: PageModular;
  keyString?: string;
  pageTypeDataLayer?: string;
  includePublisher?: boolean;
  packages?: PricingPackage[];
  minimumReportPrice?: number;
  businessPackages?: PricingPackage[];
  skipPageView?: boolean;
}

const getFixedModule = (wrappers: PageModularModuleWrapper[], type: ModuleContentType) => {
  return wrappers.find((moduleWrapper) => moduleWrapper.contentType === type)?.module;
};

const getContentModuleWrappers = (wrappers: PageModularModuleWrapper[]): PageModularModuleWrapper[] => {
  return wrappers.filter((moduleWrapper) => !fixedModules.includes(moduleWrapper.contentType));
};

export const ModularPage: React.FC<ModularPageProps> = (props) => {
  const {
    modularPage,
    keyString = "modular-page-module-wrapper",
    packages,
    minimumReportPrice,
    businessPackages,
    includePublisher,
    skipPageView,
  } = props;
  const {
    PromotionBanner,
    CampaignBanner,
    LandingPageVoucherEntry,
    ModuleSectionBlock,
    LegalDisclaimer,
    StickyVinSearch,
  } = useModuleLoaders();
  const locale = useLocale();
  const t = useTranslations("common");
  const { setBreadcrumbs } = useBreadcrumbs();

  const promotionBannerModule = useMemo(
    () => getFixedModule(modularPage.modules, ModuleContentType.ModuleBanner),
    [modularPage.modules],
  ) as ModuleBanner;

  const disclaimerModule = useMemo(
    () => getFixedModule(modularPage.modules, ModuleContentType.ModuleDisclaimer),
    [modularPage.modules],
  ) as ModuleDisclaimer;

  const campaignBannerModule = useMemo(
    () => getFixedModule(modularPage.modules, ModuleContentType.ModuleCampaignBanner),
    [modularPage.modules],
  ) as ModuleCampaignBanner;

  const voucherLandingPageBannerModule = useMemo(
    () => getFixedModule(modularPage.modules, ModuleContentType.ModuleLandingPageVoucherEntry),
    [modularPage.modules],
  ) as ModuleLandingPageVoucherEntry;

  const damageExplainerModule = useMemo(
    () => getFixedModule(modularPage.modules, ModuleContentType.ModuleDamageExplainer),
    [modularPage.modules],
  ) as ModuleDamageExplainer;

  const stickyVinSearchModule = modularPage?.stickyVinSearch as ModuleStickyVinSearch;

  const contentModuleWrappers = useMemo(() => getContentModuleWrappers(modularPage.modules), [modularPage.modules]);

  const faqModule = useMemo(() => modularPageSchemaHelpers.detectFAQModule(modularPage.modules), [modularPage.modules]);

  const lang = languageConfig.mapLocaleToLanguageShort(locale);

  const breadcrumbs = useMemo(
    () =>
      buildModularPageBreadcrumbs({
        currentPageTitle: modularPage.breadcrumbLabel || modularPage.seo?.title || "",
        currentPageSlug: modularPage.slug,
        parentPage: modularPage.parentPage,
        homeLabel: t("breadcrumb.home"),
        lang,
      }),
    [modularPage.breadcrumbLabel, modularPage.seo?.title, modularPage.slug, modularPage.parentPage, t, lang],
  );

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
    return () => setBreadcrumbs([]);
  }, [breadcrumbs, setBreadcrumbs]);

  const seoConfig = modularPage?.seo;

  const modularPageTheme = modularPage?.theme || ThemeName.DEFAULT;

  const currentLanguage = useLanguage();

  const hrefLangAlternates = buildHrefLangAlternatesForModularPage({
    slugs: modularPage.slugs,
    currentLanguage,
    robots: seoConfig?.robotsMeta,
    overrides: normalizeCmsHrefLangEntries(seoConfig?.hrefLang),
  });

  return (
    <>
      <Seo
        title={seoConfig?.title}
        description={seoConfig?.description}
        keywords={seoConfig?.keywords}
        canonical={seoConfig?.canonical}
        robotsMeta={seoConfig?.robotsMeta}
        hrefLangAlternates={hrefLangAlternates}
        image={seoConfig?.image?.fields?.file?.url}
        skipPageView={skipPageView}
        schema={{
          modularPageGraph: {
            canonicalUrl: getFullCanonicalUrl(seoConfig?.canonical) || `${CARFAX_BASE_URL}/${modularPage.slug}`,
            pageTitle: seoConfig?.title,
            metaDescription: seoConfig?.description,
            locale: locale,
            dateModified: modularPageSchemaHelpers.getDateModifiedISO(modularPage.lastUpdated, modularPage.updatedAt),
            faqModule,
            breadcrumbs: toAbsoluteBreadcrumbs(breadcrumbs),
            includePublisher,
          },
        }}
      />
      {promotionBannerModule && <PromotionBanner module={promotionBannerModule} />}
      {campaignBannerModule && <CampaignBanner module={campaignBannerModule} />}
      {voucherLandingPageBannerModule && (
        <LandingPageVoucherEntry
          module={voucherLandingPageBannerModule}
          redirectTo={() =>
            RouterService.routeWithQuery(Routes.PARTNER_PACKAGES, {
              partnerName: voucherLandingPageBannerModule.packageType,
            })
          }
        />
      )}
      {contentModuleWrappers && (
        <ModulesWithTheme themeName={modularPageTheme}>
          {contentModuleWrappers.map((moduleWrapped, index: number) => {
            if (moduleWrapped.contentType === ModuleContentType.ModuleFeatureIntro) {
              return (
                <ModularPageComponent key={`${keyString}-${index}`}>
                  <ModularPageComponentWrapper
                    indexPosition={index}
                    moduleWrapper={{
                      ...moduleWrapped,
                      module: {
                        ...(moduleWrapped.module as ModuleFeatureIntro),
                        noPaddingBottom: !!damageExplainerModule,
                      },
                    }}
                    showSecondaryImage={isEven(index)}
                    packages={packages}
                    minimumReportPrice={minimumReportPrice}
                    businessPackages={businessPackages}
                  />
                  {damageExplainerModule && (
                    <ModularPageComponentWrapper
                      indexPosition={index}
                      moduleWrapper={{
                        contentType: ModuleContentType.ModuleDamageExplainer,
                        module: damageExplainerModule,
                      }}
                      showSecondaryImage={isEven(index)}
                      packages={packages}
                      minimumReportPrice={minimumReportPrice}
                      businessPackages={businessPackages}
                    />
                  )}
                </ModularPageComponent>
              );
            }
            if (moduleWrapped.contentType === ModuleContentType.ModuleSection) {
              return (
                <ModularPageComponent key={`${keyString}-${index}`} noPadding>
                  <ModuleSectionBlock module={moduleWrapped.module as ModuleSection} indexPosition={index} />
                </ModularPageComponent>
              );
            }
            return (
              <ModularPageComponent key={`${keyString}-${index}`}>
                <ModularPageComponentWrapper
                  indexPosition={index}
                  moduleWrapper={moduleWrapped}
                  showSecondaryImage={isEven(index)}
                  packages={packages}
                  minimumReportPrice={minimumReportPrice}
                  businessPackages={businessPackages}
                />
              </ModularPageComponent>
            );
          })}
        </ModulesWithTheme>
      )}

      {disclaimerModule && <LegalDisclaimer module={disclaimerModule} />}
      {stickyVinSearchModule && <StickyVinSearch module={stickyVinSearchModule} />}
    </>
  );
};
